<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：共通ダイアログ
 *
 * 作成者：2021/11/25 ノア）黄
 * 最終修正者：2021/11/29 ノア）虞
 *
 -->
<template>
  <div class="tti-dialog-container">
    <el-dialog
      :visible.sync="isShowSelf"
      :width="width"
      :show-close="false"
      :close-on-click-modal="false"
      :top="marginTop"
    >
      <template v-if="showHead" slot="title">
        <div class="tti-dialog-header">
          <!-- タイトル -->
          <div>
            <span>{{ title }}</span>
          </div>
          <!-- マニュアル -->
          <div class="tti-dialog-header-reference" v-if="showClose">
            <el-button
              v-if="showHelp"
              @click="onHelpClick"
              v-tooltip="{
                content: `${$t('master.tooltipCommon.dialogHelpTip')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
              ><tti-svg-icon icon-name="he_007"></tti-svg-icon
            ></el-button>
            <el-button
              @click="isShowSelf = false"
              v-tooltip="{
                content: `${$t('master.tooltipCommon.dialogCloseTip')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
              ><tti-svg-icon icon-name="su_023"></tti-svg-icon
            ></el-button>
          </div>
        </div>
      </template>
      <slot name="body"></slot>
      <template slot="footer">
        <slot name="foot"> </slot>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit } from 'vue-property-decorator'

import localStorageConfig from '@/config/localStorageConfig'
import { setLocalStorage } from '@/utils/localStorageUtils'

@Component({
  name: 'ttiDialog'
})
export default class TtiDialog extends Vue {
  /**
   * ダイアログボックスを表示するかどうかのフラグ
   */
  @Prop({
    default: false
  })
  isShow!: boolean

  /**
   * ヘッダ部分を表示するかどうかのフラグ
   */
  @Prop({
    default: true
  })
  showHead!: boolean

  /**
   * ヘッダボタンを表示するかどうかのフラグ
   */
  @Prop({
    default: true
  })
  showClose!: boolean

  /**
   * Helpボタンを表示するかどうかのフラグ
   */
  @Prop({
    default: true
  })
  showHelp!: boolean

  /**
   * ダイアログの幅
   */
  @Prop({
    default: '80%'
  })
  width!: string

  /**
   * ダイアログのタイトル
   */
  @Prop({
    default: ''
  })
  title!: string

  /**
   * マニュアルのキー
   */
  @Prop({
    default: ''
  })
  manualKey!: string

  /**
   * マニュアルのキー
   */
  @Prop({
    default: '20vh'
  })
  marginTop!: string

  /**
   * 表示状態変更後の関数
   *
   * @param {boolean} status 表示状態
   * @returns {boolean} 表示状態
   */
  @Emit('handelDisplay')
  handelDisplay(status: boolean): boolean {
    return status
  }

  /**
   * ダイアログボックスを表示するかどうかを取得する（画面用）
   *
   * @returns {boolean} ダイアログボックスを表示するかどうか（画面用）
   */
  get isShowSelf(): boolean {
    return this.isShow
  }

  /**
   * ダイアログボックスを表示するかどうかを設定する（画面用）
   */
  set isShowSelf(value: boolean) {
    this.handelDisplay(value)
  }

  /**
   * マニュアル表示ボタンが押された時の処理
   *
   * @returns {void}
   */
  onHelpClick(): void {
    this.$log.debug('TtiDialog > onHelpClick: key=', this.manualKey)
    setLocalStorage(localStorageConfig.baseKey, {
      [localStorageConfig.helpFileName]: this.manualKey
    })
    window.open(this.manualKey, '_blank')
  }
}
</script>

<style scoped lang="scss">
.tti-dialog-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tti-dialog-header-reference {
  position: absolute;
  right: 0;
  top: -3px;
}

.tti-dialog-header-reference > .el-button {
  padding: 0;
  background-color: inherit;

  &:hover {
    @include bt_001_h;
  }

  &:active {
    @include bt_001_p;
  }
}

::v-deep .tti-dialog-header-reference > .el-button > i {
  font-size: 25px;
}
</style>
